import { useMemo, useState } from "react"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { CompaniesFilterNegationKey } from "./companies-filter-state"
import type { FilterType } from "./filter-popover-items/types"
import type { FilterChip } from "@/components/filters"
import { FilterControlChips } from "@/components/filters"
import { SOURCE_OPTIONS } from "@/components/filters/source-options"
import { useCompaniesFilterState } from "./companies-filter-state"
import { OwnerFilterItems } from "./filter-popover-items/owner-filter-items"
import { SourceFilterItems } from "./filter-popover-items/source-filter-items"
import { StatusFilterItems } from "./filter-popover-items/status-filter-items"
import { TierFilterItems } from "./filter-popover-items/tier-filter-items"

export function CompaniesFilterControl() {
	const {
		tiers,
		statuses,
		ownerIds,
		revenue,
		size,
		sources,
		negations,
		setNegation,
		clearTiers,
		clearStatuses,
		clearOwners,
		clearSources,
		setRevenue,
		setSize,
	} = useCompaniesFilterState()

	const [query, setQuery] = useState("")

	const negationProps = (
		key: CompaniesFilterNegationKey,
		valueCount: number,
	) => ({
		valueCount,
		isNegated: negations[key] ?? false,
		onNegatedChange: (isNegated: boolean) => {
			setNegation(key, isNegated)
		},
	})

	const onFilterItemSelect = () => {
		setTimeout(() => setQuery(""), 100)
	}

	const renderEditor = (label: FilterType) => (
		<Command>
			<CommandInput
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[70dvh]">
				<CommandEmpty>Nothing found.</CommandEmpty>
				<CommandGroup>
					{label === "Tiers" && (
						<TierFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Statuses" && (
						<StatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "Owners" && (
						<OwnerFilterItems onSelect={onFilterItemSelect} />
					)}
					{label === "CreatedBy" && (
						<SourceFilterItems onSelect={onFilterItemSelect} />
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)

	const chips = useMemo<FilterChip[]>(() => {
		const result: FilterChip[] = []
		if (statuses.length > 0) {
			result.push({
				key: "Statuses",
				label: "Status",
				...negationProps("statuses", statuses.length),
				valueText:
					statuses.length === 1 ? statuses[0]! : `${statuses.length} statuses`,
				onClear: clearStatuses,
				popoverContent: renderEditor("Statuses"),
			})
		}
		if (tiers.length > 0) {
			result.push({
				key: "Tiers",
				label: "Tier",
				...negationProps("tiers", tiers.length),
				valueText: tiers.length === 1 ? tiers[0]! : `${tiers.length} tiers`,
				onClear: clearTiers,
				popoverContent: renderEditor("Tiers"),
			})
		}
		if (ownerIds.length > 0) {
			result.push({
				key: "Owners",
				label: "Owner",
				...negationProps("ownerIds", ownerIds.length),
				valueText:
					ownerIds.length === 1
						? ownerIds[0] === null
							? "No owner"
							: "1 owner"
						: `${ownerIds.length} owners`,
				onClear: clearOwners,
				popoverContent: renderEditor("Owners"),
			})
		}
		if (sources.length > 0) {
			result.push({
				key: "CreatedBy",
				label: "Synced with",
				...negationProps("sources", sources.length),
				valueText:
					sources.length === 1
						? (SOURCE_OPTIONS.find((o) => o.value === sources[0])?.label ??
							sources[0] ??
							"")
						: `${sources.length} sources`,
				onClear: clearSources,
				popoverContent: renderEditor("CreatedBy"),
			})
		}
		return result
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tiers, statuses, ownerIds, sources, negations, query])

	const numericTail = revenue !== null || size !== null
	const anyApplied = chips.length > 0 || numericTail
	if (!anyApplied) return null

	const tail = (
		<>
			{revenue && (
				<div className="flex items-center divide-x divide-gray-300 overflow-hidden whitespace-nowrap rounded-lg border border-gray-300 bg-white text-xs dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
					<span className="px-2 py-1 text-primary">Revenue</span>
					<span className="truncate px-2 py-1 text-primary">
						{revenue.operator} ${revenue.value}
					</span>
					<button
						type="button"
						className="flex items-center self-stretch px-2 text-tertiary hover:bg-gray-150 hover:text-primary dark:hover:bg-gray-750"
						onClick={() => setRevenue(null)}
					>
						<X className="h-3 w-3" />
					</button>
				</div>
			)}
			{size && (
				<div className="flex items-center divide-x divide-gray-300 overflow-hidden whitespace-nowrap rounded-lg border border-gray-300 bg-white text-xs dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
					<span className="px-2 py-1 text-primary">Size</span>
					<span className="truncate px-2 py-1 text-primary">
						{size.operator} {size.value}
					</span>
					<button
						type="button"
						className="flex items-center self-stretch px-2 text-tertiary hover:bg-gray-150 hover:text-primary dark:hover:bg-gray-750"
						onClick={() => setSize(null)}
					>
						<X className="h-3 w-3" />
					</button>
				</div>
			)}
		</>
	)

	return <FilterControlChips chips={chips} tailContent={tail} />
}
